import { renderElement } from "../../utils/use-render.js";
import { useMenuOptionState } from "../menu/context.js";
import { MenuRadioGroup } from "../menu/radio-group.js";
import { usePlaybackRateOptions } from "../playback-rate/use-playback-rate-options.js";
import { PlaybackRateRadioGroupDataAttrs } from "@videojs/core";
import { getStateDataAttrs } from "@videojs/core/dom";
import { Fragment, createContext, forwardRef, useContext } from "react";
import { jsx } from "react/jsx-runtime";
import { isFunction } from "@videojs/utils/predicate";
//#region src/ui/playback-rate-radio-group/component.tsx
const PlaybackRateRadioGroupContext = createContext(void 0);
/** Owns playback-rate option state and shares it with an enclosing menu. Does not render a DOM element. */
function PlaybackRateRadioGroupRoot({ children, ...props }) {
	const playbackRate = usePlaybackRateOptions(props);
	useMenuOptionState(toMenuOptionState(playbackRate));
	return /* @__PURE__ */ jsx(PlaybackRateRadioGroupContext.Provider, {
		value: playbackRate,
		children
	});
}
/** Displays the selected playback-rate label. */
const PlaybackRateRadioGroupValue = forwardRef(function PlaybackRateRadioGroupValue({ render, className, style, ...elementProps }, forwardedRef) {
	const playbackRate = usePlaybackRateRadioGroupContext();
	if (!playbackRate) return null;
	return renderElement("span", {
		render,
		className,
		style
	}, {
		state: playbackRate,
		ref: forwardedRef,
		props: [elementProps, { children: playbackRate.selectedLabel }]
	});
});
/** Renders menu radio items for the player's available playback rates. */
const PlaybackRateRadioGroupOptions = forwardRef(function PlaybackRateRadioGroupOptions(componentProps, forwardedRef) {
	const { renderItem, render, className, style, "aria-label": ariaLabelProp, "aria-labelledby": ariaLabelledBy, ...elementProps } = componentProps;
	const playbackRate = usePlaybackRateRadioGroupContext();
	if (!playbackRate) return null;
	const { state, value, options, setValue } = playbackRate;
	const ariaLabel = ariaLabelProp ?? (ariaLabelledBy === void 0 ? playbackRate.label : void 0);
	return /* @__PURE__ */ jsx(MenuRadioGroup, {
		...getStateDataAttrs(state, PlaybackRateRadioGroupDataAttrs),
		...elementProps,
		ref: forwardedRef,
		className: isFunction(className) ? className(state) : className,
		style: isFunction(style) ? style(state) : style,
		render: isFunction(render) ? (props) => render(props, state) : render,
		value,
		onValueChange: setValue,
		"aria-label": ariaLabel,
		"aria-labelledby": ariaLabelledBy,
		"aria-disabled": state.disabled || void 0,
		hidden: state.hidden,
		children: options.map((option) => {
			const itemState = {
				...option,
				checked: option.value === value
			};
			return /* @__PURE__ */ jsx(Fragment, { children: renderItem({
				value: option.value,
				disabled: option.disabled,
				"data-rate": option.value,
				children: option.label
			}, itemState) }, option.value);
		})
	});
});
/** @internal Compatibility adapter for the existing preset sources. */
const PlaybackRateRadioGroupLegacy = forwardRef(function PlaybackRateRadioGroupLegacy({ label, formatRate, disabled, ...props }, forwardedRef) {
	return /* @__PURE__ */ jsx(PlaybackRateRadioGroupRoot, {
		label,
		formatRate,
		disabled,
		children: /* @__PURE__ */ jsx(PlaybackRateRadioGroupOptions, {
			...props,
			ref: forwardedRef
		})
	});
});
function usePlaybackRateRadioGroupContext() {
	const playbackRate = useContext(PlaybackRateRadioGroupContext);
	if (playbackRate === void 0) throw new Error("PlaybackRateRadioGroup parts must be used within PlaybackRateRadioGroup.Root");
	return playbackRate;
}
function toMenuOptionState(playbackRate) {
	return {
		value: playbackRate?.selectedLabel ?? "",
		disabled: playbackRate?.disabled ?? true,
		hidden: playbackRate?.hidden ?? true,
		availability: playbackRate?.state.availability ?? "unsupported"
	};
}
//#endregion
export { PlaybackRateRadioGroupLegacy, PlaybackRateRadioGroupOptions, PlaybackRateRadioGroupRoot, PlaybackRateRadioGroupValue };

//# sourceMappingURL=component.js.map